import {
  countries,
  countriesAlpha3Alpha2Lookup,
  countryKeys,
  getStateAbbreviations,
  getStateAbbrProperCase,
} from '@forms/input-forms/helpers';
import * as turf from '@turf/turf';
import { ormRegex } from './regex';
import { RequestType, statesData } from '@utils/enums';
import { resolveNprConfigForRequest } from '@src/app-pages/Forms/registry/configs/npr/nprConfig';
import { formatNprRhfAnswers } from '@api/services/npr/helpers';
import { formatNonPcnRhfAnswers } from '@api/services/non-pcn/helpers';
import * as XLSX from 'xlsx';

export const classnames = (opts) =>
  Object.keys(opts)
    .map((key) => (!!opts[key] ? key : ''))
    .join(' ');

export const classArray = (arr) => arr.filter((e) => e).join(' ');

export const formatBytes = (bytes) => {
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return 'n/a';
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10);
  if (i === 0) return `${bytes} ${sizes[i]}`;
  return `${(bytes / 1024 ** i).toFixed(1)} ${sizes[i]}`;
};

export const isNumeric = (str) => {
  if (typeof str != 'string') return false; // only process strings
  return (
    !isNaN(str) && // use type coercion to parse the entirety of the string (`parseFloat` alone does not do this)...
    !isNaN(parseFloat(str))
  ); // ...and ensure strings of whitespace fail
};

// @TODO: Delete once all references are removed from bundles
export const queryFromObject = (obj = {}) => {
  const keys = Object.keys(obj);

  if (!keys.length) return '';

  const finalKeys = keys.filter((key) => !!obj[key] || isNumeric(obj[key]));

  return `?${finalKeys.map((key) => `${key}=${obj[key]}`).join('&')}`;
};

export const isObjectNotDirty = (obj) =>
  Object.keys(obj)
    .filter((key) => key !== 'contactType')
    .some((key) => obj[key] !== undefined && obj[key] !== null && obj[key] !== '');

export const stripUncompletedContacts = (contacts) => contacts.filter((contact) => isObjectNotDirty(contact));

export const filterNullEmptyObjects = (data) => {
  if (Array.isArray(data)) {
    // Handle array elements recursively
    const filteredArray = data
      .map((item) => filterNullEmptyObjects(item))
      .filter(
        (item) =>
          !(typeof item === 'object' && item !== null && Object.keys(item).length === 0) &&
          item !== '' &&
          item !== undefined
      );
    // Return null if array is empty after filtering
    return filteredArray.length > 0 && filteredArray.some((obj) => obj !== undefined && obj !== null)
      ? filteredArray
      : null;
  } else if (typeof data === 'object' && data !== null) {
    // Create a new object for filtered properties if input is an object
    const newObj = {};
    Object.keys(data).forEach((key) => {
      const value = data[key];
      const filteredValue = filterNullEmptyObjects(value);
      if (typeof filteredValue === 'object') {
        if (filteredValue !== null && Object.keys(filteredValue).length !== 0) {
          newObj[key] = filteredValue;
        }
      } else if (filteredValue !== '' && filteredValue !== null && filteredValue !== undefined) {
        newObj[key] = filteredValue;
      }
    });
    // Return null if the object is empty after filtering
    return Object.keys(newObj).length > 0 ? newObj : undefined;
  } else {
    // Return the data directly if it is not an object or array
    return data !== '' && data !== undefined && JSON.stringify(data) !== 'null' ? data : null;
  }
};

export const formatCoordFlt = (coord) => (coord ? parseFloat(Number(coord).toFixed(7)) : null);

export const formatCoordStr = (coord) => (coord ? Number(coord).toFixed(7) : null);

export const sanitizeText = (text) => text.replace(/[^\w\sÀ-ž -~ʻ]/g, ''); // allows letters, numbers, whitespace, diacritics, punctuation, and the okina (hawaii)

export const isValidText = (text) => sanitizeText(text) === text;

export const dateBeforeA = '1896-03-03';
export const dateBeforeB = '1986-11-13';

export const isDateValid = (dateStr, startDate, endDate) => {
  const date = new Date(dateStr);
  const datetime = new Date(dateStr).getTime();
  const startDatetime = new Date(startDate).getTime();
  const endDatetime = new Date(endDate).getTime();
  const year = date?.getFullYear();

  // CASE: Year is NaN
  if (isNaN(year)) {
    return false;
  }

  // CASE: Minimum/maximum year requirement
  if (datetime < startDatetime || datetime > endDatetime) {
    return false;
  }

  // CASE: Year more than 4 numbers
  if (String(year)?.length > 4) {
    return false;
  }

  return true;
};

export const isDateRangeValid = (dateStart, dateEnd) => {
  const startTime = new Date(dateStart).getTime();
  const endTime = new Date(dateEnd).getTime();

  return startTime <= endTime;
};

export const formatDateToMMDDYYYY = (dateValue) => {
  const [year, month, day] = dateValue.split('T')[0].split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const formattedValue = date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formattedValue;
};

export const formatUSPhoneNumber = (value) => {
  const numericValue = value.replace(/\D/g, '');
  if (numericValue.length <= 3) {
    return numericValue;
  } else if (numericValue.length <= 6) {
    return `(${numericValue.slice(0, 3)}) ${numericValue.slice(3)}`;
  } else {
    return `(${numericValue.slice(0, 3)}) ${numericValue.slice(3, 6)}-${numericValue.slice(6, 10)}`;
  }
};

export const debounce = (func, delay) => {
  let timeoutId;
  return function () {
    const context = this;
    const args = arguments;
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(context, args);
    }, delay);
  };
};

export const mapCountryAlpha3toAlpha2 = (alpha3) => countriesAlpha3Alpha2Lookup[alpha3];

export const isValidORMNumber = (num) => ormRegex.test(num);

export const convertBBoxToLeafletBounds = (bbox) => {
  const [minX, minY, maxX, maxY] = bbox;
  return [
    [minY, minX],
    [maxY, maxX],
  ];
};

export const calculateBufferedExtent = (geometry, bufferMiles) => {
  const buffered = turf.buffer(geometry, bufferMiles, { units: 'miles' });
  const bbox = turf.bbox(buffered);
  return convertBBoxToLeafletBounds(bbox);
};

export const calculateMultiPointExtent = (geometry, bufferMiles) => {
  const buffered = turf.buffer(geometry, bufferMiles, { units: 'miles' });
  const bbox = turf.bbox(buffered);
  return convertBBoxToLeafletBounds(bbox);
};

export const calculatePolygonExtent = (geometry) => {
  const bbox = turf.bbox(geometry);
  return convertBBoxToLeafletBounds(bbox);
};

export const calculateLineStringExtent = (geometry) => {
  const bbox = turf.bbox(geometry);
  return convertBBoxToLeafletBounds(bbox);
};

export const calculateGeometryExtent = (geometry) => {
  const type = geometry?.type;

  switch (type) {
    case 'Point':
      return calculateBufferedExtent(geometry, 5);

    case 'MultiPoint':
      return calculateMultiPointExtent(geometry, 5);

    case 'Polygon':
    case 'MultiPolygon':
      return calculatePolygonExtent(geometry);

    case 'LineString':
    case 'MultiLineString':
      return calculateLineStringExtent(geometry);

    default:
      return null;
  }
};

export const calculateAcres = (length, width) => {
  const squareFeet = length * width;
  const acres = (squareFeet / 43560).toFixed(6);
  return parseFloat(acres);
};

export const getProposedAmount = (isDisabled, rowAmountUnit, rowLength, rowWidth) => {
  if (!isDisabled) {
    return '';
  }
  let calculatedValue;
  switch (rowAmountUnit) {
    case 'Square Feet':
      calculatedValue = rowLength * rowWidth;
      break;
    case 'Acres':
      calculatedValue = calculateAcres(rowLength, rowWidth);
      break;
    default:
      return 'N/A';
  }
  if (isNaN(calculatedValue) || calculatedValue === 0) {
    return 'N/A';
  } else {
    return calculatedValue.toString();
  }
};

export const scrollToElement = (elementId) => {
  setTimeout(() => {
    document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth' });
  }, 100);
};

export const isLatLngOutsideUs = (lat, lng) => {
  // Check if latitude is outside the Northern Hemisphere (0 to 90 degrees)
  if (Number(lat) >= -90 && Number(lat) <= 0) {
    return true;
  }
  // Check if longitude is outside the Western Hemisphere (-180 to 0 degrees)
  if (Number(lng) >= 0 && Number(lng) <= 180) {
    return true;
  }
  return false;
};

export const getLabelTextById = (id) => {
  const label = document.querySelector(`.usa-label span[id="${id}_label"]`);
  const labelText = label?.textContent;

  // Set custom label for State/Region and Zip/Postal Code fields
  let text = '';
  if (['State', 'Region'].includes(labelText)) {
    text = 'State/Region';
  } else if (['zip code', 'postal code'].includes(labelText?.toLowerCase())) {
    text = 'Zip/Postal Code';
  } else {
    text = labelText;
  }

  return label ? text : undefined;
};

export const compareArrays = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export const getStateNameFromCode = (code) => getStateAbbrProperCase(statesData)?.[code] || code || '';

export const getCountryNameFromCode = (code) => countryKeys?.[code] || code || '';

export const toTitleCase = (str) => str?.toLowerCase().replace(/\b\w/g, (match) => match.toUpperCase()) || '';

export const formatReadOnlyValue = (value) => {
  if (value === true || value === 'true') return 'Yes';
  if (value === false || value === 'false') return 'No';

  const stateName = getStateNameFromCode(value);

  if (stateName) {
    const findStateName = getStateAbbreviations(statesData);
    const matchedState = findStateName[stateName];
    const findCountry = countries.find((country) => country.value === stateName);
    const matchedCountry = findCountry?.text;
    const formattedState = toTitleCase(matchedState);

    return formattedState || matchedCountry || stateName;
  }

  const countryName = getCountryNameFromCode(value);
  if (countryName) return countryName;

  return value || '';
};

export const doClearIncompletePhoneInfo = (payload) => {
  const contacts = payload?.request?.contacts;

  const filteredContacts = contacts?.map((contact) => {
    let updatedContact = { ...contact };

    // Check phoneNumberOne and remove country code if empty
    if (!contact?.phoneOne || contact?.phoneOne?.trim() === '') {
      updatedContact.phoneOneCountryCode = '';
    }

    // Check phoneNumberTwo and remove country code if empty
    if (!contact?.phoneTwo || contact?.phoneTwo?.trim() === '') {
      updatedContact.phoneTwoCountryCode = '';
    }

    // Check faxPhone and remove country code if empty
    if (!contact?.faxPhone || contact?.faxPhone?.trim() === '') {
      updatedContact.faxCountryCode = '';
    }

    return updatedContact;
  });

  return {
    ...payload,
    request: {
      ...payload.request,
      contacts: filteredContacts,
    },
  };
};

export const fetchWithTimeout = async (resource, options, timeout = 10000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(resource, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    throw error;
  }
};

export const mapStateRegulation = (regulations) =>
  regulations.map(({ stateABBR, state }) => ({
    text: state,
    value: stateABBR,
  }));

export const formatUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
};

export const validateCsvHeaders = async (csvHeaders, columnHeaders, ignoredHeaders = []) => {
  const columnHeadersNoCheckbox = columnHeaders.slice(1); //Remove checkbox column
  const columnsHeadersNoIgnored = columnHeadersNoCheckbox.filter((header) => !ignoredHeaders.includes(header));
  const columnHeadersLowerCase = columnsHeadersNoIgnored?.map((header) => header?.toLowerCase());
  const csvHeadersLowerCase = csvHeaders.map((header) => (typeof header === 'string' ? header.toLowerCase() : header));
  const headersMatch =
    csvHeadersLowerCase.length === columnHeadersLowerCase.length &&
    csvHeadersLowerCase.slice().sort().join(',') === columnHeadersLowerCase.slice().sort().join(',');
  // Validate if CSV headers match expected
  if (!headersMatch) {
    return false;
  }
  return true;
};

const readFile = async (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e.target.error);
    reader.readAsArrayBuffer(file);
  });

export const parseCsvWithXlsx = async (fileNames) => {
  if (!fileNames || fileNames.length === 0) return;

  const allRowData = [];
  for (const file of fileNames) {
    const fileContents = await readFile(file);
    const workbook = XLSX.read(fileContents, { type: 'buffer', raw: true });
    workbook.SheetNames.forEach((sheetName) => {
      if (sheetName?.toLowerCase() === 'lookup') return; //Skip lookup sheet
      const sheet = workbook.Sheets[sheetName];
      for (const cell in sheet) {
        if (cell[0] === '!') continue;
        if (sheet[cell].v !== undefined && sheet[cell].v !== null) {
          sheet[cell].v = String(sheet[cell].v).trim();
          sheet[cell].t = 's';
        }
      }
      const rowObject = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true });
      allRowData.push({ sheetName, rowObject });
    });
    const flattenedData = allRowData.flatMap((entry) => entry.rowObject);
    return flattenedData;
  }
};

export const resolveRequestConfig = (data, requestType, formVersion) => {
  if (requestType === RequestType.NoPermitRequired || requestType === RequestType.ActivityBasedNoPermitRequired) {
    const cfg = resolveNprConfigForRequest({ requestType, formVersion });
    return {
      defaultValues: formatNprRhfAnswers(data, {
        questionsById: cfg.questionsById,
        nprQuestionIdByName: cfg.nprQuestionIdByName,
      }),
    };
  } else if (requestType === RequestType.NonPreconstructionNotification) {
    return { defaultValues: formatNonPcnRhfAnswers(data) };
  } else {
    return { defaultValues: {} };
  }
};
